import { useContext, useState, useCallback } from 'react';
import { AppContext } from 'contexts/AppContext';
import { apiPost } from './apiPost';

/**
 * Data access for the Outcomes tab.
 *
 * /send/outcomes-summary  → headline figures
 * /send/top-products      → { shown, added } — two lists, deliberately unjoined
 * /send/entry-points      → where users open the assistant
 *
 * `conversion_rate` comes back null and stays null. The catalog JSON stores
 * brand-prefixed SKUs ("HPPSP2670007X10") while the cart table stores display
 * names ("Hayward Trivac 700 … | TVP700C"); 0 of 117 match outright and only
 * 57 of 100 match after splitting on the pipe. The 43% that miss are
 * indistinguishable from genuine non-conversions, so a CTR built on that join
 * is understated by an unknown amount while looking perfectly plausible.
 *
 * Nothing here coerces null to 0.
 */

const PAGE_SIZE = 15;

export const useOutcomesData = () => {
  const { dateRange, selectedClient, getAccessTokenSilently, base_url, platformFilter, environmentFilter } = useContext(AppContext);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [shown, setShown] = useState([]);
  const [added, setAdded] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [entryPoints, setEntryPoints] = useState([]);
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryError, setEntryError] = useState(null);

  const post = useCallback(
    (path, body) => apiPost(base_url, getAccessTokenSilently, path, body),
    [base_url, getAccessTokenSilently],
  );

  // dateRange is an ARRAY [from, to] of Dates, not {from,to} — AppContext.js:152.
  const range = useCallback(() => {
    const [from, to] = Array.isArray(dateRange) ? dateRange : [];
    if (!from || !to || !selectedClient?.key) return null;
    return {
      start_date: new Date(from).toISOString().split('T')[0],
      end_date: new Date(to).toISOString().split('T')[0],
      client: selectedClient.key,
      // Sent from one place so no individual fetch can forget it. Endpoints
      // that cannot honour it ignore it and report so in their response.
      ...(platformFilter ? { platform: platformFilter } : {}),
      // Always explicit -- 'prod' | 'stage' | 'all' -- so what the buttons show
      // is exactly what the query ran with. Absent would also mean 'prod' (the
      // backend default), but an implicit default is how filters get dropped.
      environment: environmentFilter || 'all',
    };
  }, [dateRange, selectedClient, platformFilter, environmentFilter]);

  const fetchSummary = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await post('outcomes-summary',
        { ...base, exclude_internal: excludeInternal, internal_only: internalOnly });
      setSummary(data?.data || null);
    } catch (err) {
      setSummaryError(err.response?.data?.detail || err.message || 'Failed to load outcomes');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [range, post]);

  const fetchProducts = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setProductsLoading(true);
    try {
      const data = await post('top-products',
        { ...base, limit: PAGE_SIZE, exclude_internal: excludeInternal, internal_only: internalOnly });
      setShown(data?.data?.shown || []);
      setAdded(data?.data?.added || []);
    } catch {
      setShown([]);
      setAdded([]);
    } finally {
      setProductsLoading(false);
    }
  }, [range, post]);

  // A FAILED request and an EMPTY result are different facts and must not
  // render the same way. This previously swallowed the error into an empty
  // array, and the card then said "No widget opens recorded in this range."
  //
  // That is not a broken card, it is a false finding: the endpoint was
  // returning 500 on every request, and the console told the reader there had
  // been no activity. An empty state asserts something about the business; an
  // error state asserts something about the console. Never show the first when
  // you mean the second.
  const fetchEntryPoints = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setEntryLoading(true);
    setEntryError(null);
    try {
      const data = await post('entry-points', { ...base, limit: PAGE_SIZE, exclude_internal: excludeInternal, internal_only: internalOnly });
      setEntryPoints(data?.data || []);
    } catch (err) {
      setEntryPoints([]);
      setEntryError(err.response?.data?.detail || err.message || 'Request failed');
    } finally {
      setEntryLoading(false);
    }
  }, [range, post]);

  return {
    summary, summaryLoading, summaryError, fetchSummary,
    shown, added, productsLoading, fetchProducts,
    entryPoints, entryLoading, entryError, fetchEntryPoints,
  };
};

export default useOutcomesData;
